/**
 * useGladiaTranscription
 * ----------------------
 * Hook React qui orchestre le Y-Mixer audio pour capturer l'audio
 * WebRTC depuis un <video> et le streamer vers Gladia.
 *
 * Architecture :
 *   <video> → createMediaElementSource → Y-split
 *     ├→ ctx.destination (speakers)
 *     └→ AnalyserGladia → ScriptProcessor → PCM Int16 → Gladia WS
 *
 * Two-phase start:
 *   1. startMonitor() — creates Y-Mixer + AnalyserNodes (waveform debug)
 *   2. start() — connects Gladia WS, wires PCM callback from mixer.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { AudioYMixer } from "../lib/AudioYMixer";
import { apiFetch } from "../services/api";
import {
  GladiaWebSocketClient,
  type GladiaTranscript,
} from "../lib/GladiaWebSocketClient";
import { createLogger } from "../lib/logger";
import {
  appendTranscriptBatch,
  loadTranscriptHistory,
  type PersistedTranscriptEntry,
  type TranscriptSegmentPayload,
} from "../services/transcripts";
import {
  captureSessionLiveCheckpoint,
  LiveCheckpointError,
} from "../services/liveCheckpoint";
const slog = createLogger("gladia");
/** Les journaux de checkpoint ne sont pas des journaux de transcription. */
const cplog = createLogger("checkpoint");

const TAB_CAPTURE_INTERVAL_MS = 15_000;
/** Cadence du déclencheur ; l'échéance réelle vit dans `nextCaptureAtRef`. */
const CAPTURE_TICK_MS = 3_000;
/** Cadence de persistance du transcript — indépendante de la capture. */
const FLUSH_TICK_MS = 3_000;
/** Plafond local du backoff transitoire, quel que soit `retry_after_ms`. */
const CAPTURE_BACKOFF_MAX_MS = 30_000;

/**
 * États fermés de la signalisation, vus par le hook (tâche 201).
 *
 * Le hook ne doit rien inférer d'un `MediaStream` résiduel : il lit un état.
 */
export type SignalingState =
  | "idle"
  | "connecting"
  | "open"
  | "reconnecting"
  | "closed"
  | "failed";

export interface TranscriptionLifecycle {
  /** État métier de la session (`on`, `ongoing`, `stopping`, `off`…). */
  sessionState?: string | null;
  /** État fermé de la chaîne de signalisation. */
  signalingState?: SignalingState;
}

/** Seuls ces états autorisent une capture d'onglets. */
const CAPTURABLE_SESSION_STATES: ReadonlySet<string> = new Set([
  "on",
  "connecting",
  "ongoing",
]);

/**
 * Réarmement du verrou terminal : uniquement une transition métier explicite
 * depuis un état non capturable vers l'un de ceux-ci.
 */
const REARM_SESSION_STATES: ReadonlySet<string> = new Set([
  "starting",
  "on",
  "connecting",
  "ongoing",
]);

/** Signalisation ouverte, ou reconnexion bornée en cours. */
const CAPTURE_OPEN_SIGNALING: ReadonlySet<SignalingState> = new Set<SignalingState>([
  "open",
  "reconnecting",
]);

/** Une annulation n'est pas une panne : elle ne journalise ni ne réarme rien. */
function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError")
  );
}

/** Send a debug log line to the server via GET (nginx access log). */
function serverLog(msg: string) {
  const encoded = encodeURIComponent(msg);
  fetch(`/gladia-debug?t=${Date.now()}&m=${encoded}`, { method: "GET" }).catch(
    () => {},
  );
  slog.info(msg);
}

/** A single transcript segment (compatible with TranscriptPanel). */
export interface TranscriptEntry {
  text: string;
  timestamp: number;
  isFinal: boolean;
  language?: string;
  id?: number;
  sessionId?: string;
  seq?: number;
  url?: string | null;
}

interface PendingTranscriptEntry extends TranscriptSegmentPayload {
  session_id: string;
}

export function transcriptStorageKey(contextId: string | null, sessionId: string): string {
  return `streaming:transcript:pending:${contextId || `legacy:${sessionId}`}`;
}

function transcriptIdentity(entry: TranscriptEntry): string {
  if (entry.sessionId && entry.seq !== undefined) {
    return `${entry.sessionId}:${entry.seq}`;
  }
  if (entry.id !== undefined) return `id:${entry.id}`;
  return `local:${entry.timestamp}:${entry.text}`;
}

/** Merge without confusing equal per-session sequence numbers across sessions. */
export function mergeTranscriptEntries(
  ...groups: TranscriptEntry[][]
): TranscriptEntry[] {
  const merged = new Map<string, TranscriptEntry>();
  for (const entry of groups.flat()) {
    const key = transcriptIdentity(entry);
    const previous = merged.get(key);
    merged.set(key, previous ? { ...previous, ...entry } : entry);
  }
  return [...merged.values()].sort((a, b) => {
    if (a.id !== undefined && b.id !== undefined) return a.id - b.id;
    return a.timestamp - b.timestamp;
  });
}

function fromPersisted(entry: PersistedTranscriptEntry): TranscriptEntry {
  return {
    id: entry.id,
    sessionId: entry.session_id,
    seq: entry.seq,
    text: entry.text,
    timestamp: entry.ts_ms,
    isFinal: entry.is_final,
    url: entry.url,
  };
}

function fromPending(entry: PendingTranscriptEntry): TranscriptEntry {
  return {
    sessionId: entry.session_id,
    seq: entry.seq,
    text: entry.text,
    timestamp: entry.ts_ms,
    isFinal: true,
    url: entry.url,
  };
}

function readPending(key: string): PendingTranscriptEntry[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is PendingTranscriptEntry =>
        typeof entry === "object" &&
        entry !== null &&
        typeof entry.session_id === "string" &&
        typeof entry.seq === "number" &&
        typeof entry.text === "string" &&
        typeof entry.ts_ms === "number" &&
        entry.is_final === true,
    );
  } catch {
    return [];
  }
}

/** Return value of the hook. */
export interface UseGladiaTranscriptionResult {
  transcripts: TranscriptEntry[];
  currentPartial: string;
  isListening: boolean;
  error: string | null;
  start: () => void;
  stop: () => void;
  startMonitor: () => void;
  stopMonitor: () => void;
  setSpeakersMuted: (muted: boolean) => void;
  setSpeakerVolume: (vol: number) => void;
  enabled: boolean;
  debugInfo: string;
  monitoring: boolean;
  analyserInput: AnalyserNode | null;
  analyserSpeakers: AnalyserNode | null;
  analyserGladia: AnalyserNode | null;
}

export function useGladiaTranscription(
  stream: MediaStream | null,
  language: string = "automatic",
  sessionId: string = "",
  contextId: string | null = null,
  _sessionToken: string = "",
  lifecycle: TranscriptionLifecycle = {},
): UseGladiaTranscriptionResult {
  // Défaut volontairement fermé : sans état métier ni signalisation, la capture
  // est interdite. Un appelant qui oublie de les fournir n'obtient pas
  // l'ancienne boucle, il n'obtient aucune capture.
  const sessionState = lifecycle.sessionState ?? null;
  const signalingState: SignalingState = lifecycle.signalingState ?? "idle";
  const [transcripts, setTranscripts] = useState<TranscriptEntry[]>([]);
  const [currentPartial, setCurrentPartial] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [monitoring, setMonitoring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState("");
  const [analyserInput, setAnalyserInput] = useState<AnalyserNode | null>(null);
  const [analyserSpeakers, setAnalyserSpeakers] = useState<AnalyserNode | null>(null);
  const [analyserGladia, setAnalyserGladia] = useState<AnalyserNode | null>(null);

  const mixerRef = useRef<AudioYMixer | null>(null);
  const gladiaRef = useRef<GladiaWebSocketClient | null>(null);
  const pendingRef = useRef<PendingTranscriptEntry[]>([]);
  const nextSeqRef = useRef(0);
  const flushInFlightRef = useRef(false);
  const captureInFlightRef = useRef(false);
  const lastTabCaptureAtRef = useRef(0);
  // ── Cycle de vie de la capture (tâche 201) ────────────────────────────────
  /** Requête de capture en vol, annulable à l'instant où elle devient interdite. */
  const captureAbortRef = useRef<AbortController | null>(null);
  /** Génération : une résolution tardive d'une génération périmée est inerte. */
  const captureGenerationRef = useRef(0);
  /**
   * Verrou terminal par session : aucune capture jusqu'à réarmement explicite.
   *
   * L'état pilote le rendu (donc l'armement du déclencheur), la référence sert
   * aux décisions synchrones prises dans les callbacks asynchrones — poser le
   * verrou uniquement dans une référence laisserait le timer armé jusqu'au
   * prochain rendu.
   */
  const [terminalLatch, setTerminalLatch] = useState<string | null>(null);
  const terminalLatchRef = useRef<string | null>(null);
  /** Prochaine échéance de capture ; `Infinity` = désarmée. */
  const nextCaptureAtRef = useRef(0);
  const transientFailuresRef = useRef(0);
  const lastCaptureErrorCodeRef = useRef<string | null>(null);
  const captureAllowedRef = useRef(false);
  const previousSessionStateRef = useRef<string | null>(null);
  const lastActiveUrlRef = useRef<string | null>(null);
  const activeSessionRef = useRef(sessionId);
  const historyReadyRef = useRef<Promise<void>>(Promise.resolve());
  const storageKey = transcriptStorageKey(contextId, sessionId);
  activeSessionRef.current = sessionId;

  // v1.10.0 : disponible si le serveur a une clé Gladia (la page n'en a plus).
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    apiFetch("/transcription/status")
      .then((res) => (res.ok ? res.json() : { enabled: false }))
      .then((body: { enabled?: unknown }) => {
        if (!cancelled) setEnabled(body?.enabled === true);
      })
      .catch(() => {
        if (!cancelled) setEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const persistPendingLocally = useCallback(() => {
    if (!sessionId) return;
    try {
      if (pendingRef.current.length === 0) {
        localStorage.removeItem(storageKey);
      } else {
        localStorage.setItem(storageKey, JSON.stringify(pendingRef.current));
      }
    } catch {
      // localStorage can be unavailable in hardened/private browser contexts.
    }
  }, [sessionId, storageKey]);

  const flushPending = useCallback(async (activeUrl: string | null = null) => {
    if (!sessionId || flushInFlightRef.current) return;
    const batch = pendingRef.current
      .filter((entry) => entry.session_id === sessionId)
      .slice(0, 100);
    if (batch.length === 0) return;

    flushInFlightRef.current = true;
    try {
      const payload = batch.map(({ session_id: _sessionId, ...entry }) => ({ ...entry }));
      if (activeUrl) payload[payload.length - 1]!.url = activeUrl;
      await appendTranscriptBatch(sessionId, payload);

      // A route transition may complete while the old session flush is in flight.
      // The old POST succeeded, but its completion must not rewrite the new
      // session's pending buffer or localStorage key.
      if (activeSessionRef.current !== sessionId) return;
      const persisted = new Set(batch.map((entry) => `${entry.session_id}:${entry.seq}`));
      pendingRef.current = pendingRef.current.filter(
        (entry) => !persisted.has(`${entry.session_id}:${entry.seq}`),
      );
      persistPendingLocally();
      if (activeUrl) {
        const last = batch[batch.length - 1]!;
        setTranscripts((previous) =>
          previous.map((entry) =>
            entry.sessionId === last.session_id && entry.seq === last.seq
              ? { ...entry, url: activeUrl }
              : entry,
          ),
        );
      }
    } catch (flushError) {
      slog.warn("Transcript flush deferred", {
        sessionId,
        error: flushError instanceof Error ? flushError.message : "unknown error",
      });
    } finally {
      flushInFlightRef.current = false;
    }
  }, [persistPendingLocally, sessionId]);

  /** Annule tout travail de capture : requête en vol ET prochaine échéance. */
  const cancelCaptureWork = useCallback(() => {
    captureAbortRef.current?.abort();
    captureAbortRef.current = null;
    nextCaptureAtRef.current = Number.POSITIVE_INFINITY;
  }, []);

  /** Pose ou lève le verrou terminal, référence et état ensemble. */
  const setLatch = useCallback((value: string | null) => {
    terminalLatchRef.current = value;
    setTerminalLatch(value);
  }, []);

  /**
   * Politique d'erreur de capture (tâche 201, P1).
   *
   * Terminal → verrou de session, annulation immédiate, plus aucune tentative.
   * Transitoire → `retry_after_ms` respecté puis borné localement, avec un
   * journal dédupliqué : la boucle d'origine a produit 1 076 avertissements
   * identiques après l'arrêt.
   */
  const handleCaptureFailure = useCallback(
    (captureError: unknown) => {
      const checkpointError =
        captureError instanceof LiveCheckpointError ? captureError : null;
      const errorCode = checkpointError?.errorCode ?? "unknown";
      const changed = lastCaptureErrorCodeRef.current !== errorCode;
      lastCaptureErrorCodeRef.current = errorCode;

      if (checkpointError?.terminal) {
        setLatch(sessionId);
        captureAllowedRef.current = false;
        cancelCaptureWork();
        cplog.info("Tab-state capture disarmed for this session", {
          sessionId,
          errorCode,
          sessionState: checkpointError.sessionState ?? sessionState,
        });
        return;
      }

      const attempt = (transientFailuresRef.current += 1);
      const base = checkpointError?.retryAfterMs ?? TAB_CAPTURE_INTERVAL_MS;
      const delay = Math.min(base * 2 ** (attempt - 1), CAPTURE_BACKOFF_MAX_MS);
      nextCaptureAtRef.current = Date.now() + delay;
      if (changed) {
        cplog.warn("Tab-state capture deferred", {
          sessionId,
          errorCode,
          retryInMs: delay,
          error:
            captureError instanceof Error ? captureError.message : "unknown error",
        });
      }
    },
    [cancelCaptureWork, sessionId, sessionState, setLatch],
  );

  /**
   * Éligibilité de capture (tâche 201, P0).
   *
   * L'ancienne condition était `stream !== null` : un flux non nul survivait à
   * `ws.onclose`, donc les captures continuaient après l'arrêt de la session —
   * 1 075 conflits HTTP en 5 h 23. L'éligibilité se lit maintenant sur l'état
   * MÉTIER et l'état FERMÉ de la signalisation, jamais sur un objet résiduel.
   */
  const captureAllowed =
    sessionId !== "" &&
    CAPTURABLE_SESSION_STATES.has(sessionState ?? "") &&
    CAPTURE_OPEN_SIGNALING.has(signalingState) &&
    terminalLatch !== sessionId;
  captureAllowedRef.current = captureAllowed;

  /**
   * Capture d'onglets seule — la persistance du transcript n'en dépend plus.
   *
   * Le couplage d'origine faisait disparaître le flush final dès que le
   * checkpoint était interdit : le transcript devait donc attendre une capture
   * qui n'arriverait jamais.
   */
  const captureTabs = useCallback(
    async (force = false) => {
      if (!sessionId || captureInFlightRef.current) return;
      if (!captureAllowedRef.current) return;
      const now = Date.now();
      if (!force && now < nextCaptureAtRef.current) return;

      const generation = captureGenerationRef.current;
      const controller = new AbortController();
      captureAbortRef.current = controller;
      captureInFlightRef.current = true;
      lastTabCaptureAtRef.current = now;
      nextCaptureAtRef.current = now + TAB_CAPTURE_INTERVAL_MS;
      try {
        const capture = await captureSessionLiveCheckpoint(sessionId, {
          signal: controller.signal,
        });
        // Résolution tardive d'une génération périmée : inerte par contrat.
        if (
          generation !== captureGenerationRef.current ||
          activeSessionRef.current !== sessionId
        ) {
          return;
        }
        transientFailuresRef.current = 0;
        lastCaptureErrorCodeRef.current = null;
        lastActiveUrlRef.current = capture.active_url;
      } catch (captureError) {
        if (generation !== captureGenerationRef.current) return;
        if (isAbortError(captureError)) return;
        handleCaptureFailure(captureError);
      } finally {
        if (captureAbortRef.current === controller) {
          captureAbortRef.current = null;
        }
        captureInFlightRef.current = false;
      }
    },
    [handleCaptureFailure, sessionId],
  );

  // Hydrate server history and the crash-safe local buffer on session mount.
  useEffect(() => {
    let cancelled = false;
    lastActiveUrlRef.current = null;
    lastTabCaptureAtRef.current = 0;
    pendingRef.current = sessionId ? readPending(storageKey) : [];

    const localEntries = pendingRef.current.map(fromPending);
    setTranscripts(localEntries);
    const currentLocalSeqs = pendingRef.current
      .filter((entry) => entry.session_id === sessionId)
      .map((entry) => entry.seq);
    nextSeqRef.current =
      currentLocalSeqs.length > 0 ? Math.max(...currentLocalSeqs) + 1 : 0;

    if (sessionId) {
      historyReadyRef.current = loadTranscriptHistory(sessionId)
        .then((history) => {
          if (cancelled) return;
          const persisted = history.map(fromPersisted);
          setTranscripts((previous) => mergeTranscriptEntries(persisted, previous));
          const currentSeqs = history
            .filter((entry) => entry.session_id === sessionId)
            .map((entry) => entry.seq);
          if (currentSeqs.length > 0) {
            nextSeqRef.current = Math.max(nextSeqRef.current, Math.max(...currentSeqs) + 1);
          }
        })
        .catch((loadError) => {
          slog.warn("Transcript history load deferred", {
            sessionId,
            error: loadError instanceof Error ? loadError.message : "unknown error",
          });
        });
    } else {
      historyReadyRef.current = Promise.resolve();
    }

    return () => {
      cancelled = true;
    };
  }, [sessionId, storageKey]);

  // ── Cycle de vie de la capture (tâche 201) ────────────────────────────────

  // Changement de session : nouvelle génération, verrou et compteurs remis à
  // zéro, requête de l'ancienne session annulée. Au démontage, la génération
  // avance encore : une réponse tardive ne peut plus rien réarmer.
  useEffect(() => {
    captureGenerationRef.current += 1;
    setLatch(null);
    transientFailuresRef.current = 0;
    lastCaptureErrorCodeRef.current = null;
    nextCaptureAtRef.current = 0;
    previousSessionStateRef.current = null;
    return () => {
      captureGenerationRef.current += 1;
      cancelCaptureWork();
    };
  }, [cancelCaptureWork, sessionId, setLatch]);

  // Le verrou terminal ne se relève que sur une transition métier explicite
  // depuis un état non capturable — jamais sur le retour d'un `MediaStream`.
  useEffect(() => {
    const previous = previousSessionStateRef.current;
    previousSessionStateRef.current = sessionState;
    if (previous === null || previous === sessionState) return;
    const wasCapturable = CAPTURABLE_SESSION_STATES.has(previous);
    const isRearming = REARM_SESSION_STATES.has(sessionState ?? "");
    if (!wasCapturable && isRearming && terminalLatchRef.current === sessionId) {
      setLatch(null);
      transientFailuresRef.current = 0;
      lastCaptureErrorCodeRef.current = null;
      nextCaptureAtRef.current = 0;
      cplog.info("Tab-state capture re-armed after a business transition", {
        sessionId,
        from: previous,
        to: sessionState,
      });
    }
  }, [sessionId, sessionState, setLatch]);

  // Dès que la capture n'est plus permise, la requête en vol est annulée et la
  // prochaine échéance disparaît : plus aucun `POST /live-checkpoint`.
  useEffect(() => {
    if (captureAllowed) return;
    cancelCaptureWork();
  }, [cancelCaptureWork, captureAllowed]);

  // Déclencheur de capture : armé UNIQUEMENT tant que la capture est permise.
  useEffect(() => {
    if (!captureAllowed) return;
    const timer = window.setInterval(() => {
      void captureTabs();
    }, CAPTURE_TICK_MS);
    return () => window.clearInterval(timer);
  }, [captureAllowed, captureTabs]);

  // Persistance du transcript : indépendante de la capture, elle survit à un
  // checkpoint interdit — c'est ce qui garantit le transcript final.
  useEffect(() => {
    if (!sessionId) return;
    const timer = window.setInterval(() => {
      void flushPending(lastActiveUrlRef.current);
    }, FLUSH_TICK_MS);
    return () => window.clearInterval(timer);
  }, [flushPending, sessionId]);

  // ── Cleanup everything ────────────────────────────────────────
  const cleanup = useCallback(() => {
    gladiaRef.current?.stop();
    if (mixerRef.current) {
      mixerRef.current.onPCM(null);
      mixerRef.current.destroy();
    }
    gladiaRef.current = null;
    mixerRef.current = null;
    setIsListening(false);
    setMonitoring(false);
    setAnalyserInput(null);
    setAnalyserSpeakers(null);
    setAnalyserGladia(null);
  }, []);

  // ── Phase 1: Start Y-Mixer only (audio monitoring / waveforms) ──
  const startMonitor = useCallback(async () => {
    if (!stream || mixerRef.current) return;

    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack || audioTrack.readyState !== 'live') {
      serverLog(`START MONITOR — no live audio track (tracks=${stream.getAudioTracks().length})`);
      setError("No live audio track available");
      return;
    }

    setError(null);

    try {
      serverLog(
        `START MONITOR — stream audio tracks: ${stream.getAudioTracks().length}, ` +
        `track id=${audioTrack.id} muted=${audioTrack.muted} readyState=${audioTrack.readyState}`
      );

      const mixer = new AudioYMixer({
        targetSampleRate: 16000,
        enableSpeakers: true,
      });
      await mixer.resume();
      mixer.connectTrack(audioTrack);
      mixerRef.current = mixer;

      setAnalyserInput(mixer.analyserInput);
      setAnalyserSpeakers(mixer.analyserSpeakers);
      setAnalyserGladia(mixer.analyserGladia);
      setMonitoring(true);
      setDebugInfo("Y-Mixer active — audio monitoring");
      serverLog(`Y-Mixer active: state=${mixer.state} nativeSR=${mixer.nativeSampleRate}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to start audio monitor";
      setError(msg);
      serverLog(`MONITOR ERROR: ${msg}`);
    }
  }, [stream]);

  const stopMonitor = useCallback(() => {
    cleanup();
    setCurrentPartial("");
  }, [cleanup]);

  // ── Phase 2: Start Gladia on top of existing mixer ────────────
  const start = useCallback(async () => {
    if (!stream || !enabled) return;

    setError(null);

    try {
      // A reload of the same session must learn the largest persisted seq before
      // the first new final segment is allocated; otherwise seq=0 could collide
      // and be discarded by the idempotence constraint.
      await historyReadyRef.current;

      // Ensure mixer is running first
      if (!mixerRef.current) {
        await startMonitor();
      }

      const mixer = mixerRef.current;
      if (!mixer) {
        throw new Error("Y-Mixer failed to start");
      }

      // Start Gladia client (session + WS)
      const gladia = new GladiaWebSocketClient({
        sampleRate: 16000,
        language,
      });
      gladiaRef.current = gladia;

      await gladia.start(
        (transcript: GladiaTranscript) => {
          const text = transcript.text ?? "";
          serverLog(
            `TRANSCRIPT: "${text.slice(0, 80)}" [${transcript.language ?? "?"}] ` +
            `final=${transcript.isFinal}`
          );

          if (text) {
            if (transcript.isFinal) {
              const timestamp = Date.now();
              const seq = nextSeqRef.current++;
              const entry: TranscriptEntry = {
                text,
                timestamp,
                isFinal: true,
                language: transcript.language,
                sessionId,
                seq,
              };
              setTranscripts((previous) => mergeTranscriptEntries(previous, [entry]));
              if (sessionId) {
                pendingRef.current.push({
                  session_id: sessionId,
                  seq,
                  text,
                  ts_ms: timestamp,
                  is_final: true,
                });
                persistPendingLocally();
                if (
                  pendingRef.current.filter((item) => item.session_id === sessionId).length >= 5
                ) {
                  void flushPending(lastActiveUrlRef.current);
                }
              }
              setCurrentPartial("");
            } else {
              setCurrentPartial(text);
            }
          }

          setDebugInfo(
            `${transcript.isFinal ? "FINAL" : "partial"}: "${text.slice(0, 50)}" ` +
            `[${transcript.language ?? "?"}]`
          );
        },
        (err: Error) => {
          serverLog(`Gladia error: ${err.message}`);
          setError(err.message);
        },
      );

      // Wire PCM from mixer → Gladia WS
      mixer.onPCM((pcmData) => {
        gladia.sendPCM(pcmData);
      });

      setIsListening(true);
      setDebugInfo("Y-Mixer → Gladia streaming active (wav/pcm 16kHz)");
      serverLog("Capture started — Y-Mixer → ScriptProcessor(PCM Int16) → Gladia WS (wav/pcm)");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to start transcription";
      setError(msg);
      serverLog(`START ERROR: ${msg}`);
    }
  }, [
    stream,
    enabled,
    startMonitor,
    language,
    sessionId,
    persistPendingLocally,
    flushPending,
  ]);

  const stop = useCallback(() => {
    // Stop only Gladia, keep mixer running for waveforms.
    // Le transcript final part TOUJOURS ; la capture d'onglets n'est tentée que
    // si elle est encore permise (tâche 201 : découplage flush / capture).
    void flushPending(lastActiveUrlRef.current);
    if (captureAllowedRef.current) {
      void captureTabs(true);
    }
    if (mixerRef.current) {
      mixerRef.current.onPCM(null);
    }
    gladiaRef.current?.stop();
    gladiaRef.current = null;
    setIsListening(false);
    setCurrentPartial("");
    if (monitoring) {
      setDebugInfo("Y-Mixer active — Gladia stopped");
    }
  }, [captureTabs, flushPending, monitoring]);

  // Restart Gladia when language changes while listening
  const prevLangRef = useRef(language);
  useEffect(() => {
    if (prevLangRef.current !== language && isListening) {
      prevLangRef.current = language;
      serverLog(`Language changed to "${language}" — restarting Gladia session`);
      // Stop current Gladia session (keep mixer)
      if (mixerRef.current) {
        mixerRef.current.onPCM(null);
      }
      gladiaRef.current?.stop();
      gladiaRef.current = null;
      setIsListening(false);
      setCurrentPartial("");
      // Restart with new language (next tick so state updates)
      setTimeout(() => start(), 50);
    }
    prevLangRef.current = language;
  }, [language, isListening, start]);

  const setSpeakersMuted = useCallback((muted: boolean) => {
    mixerRef.current?.setSpeakersMuted(muted);
  }, []);

  const setSpeakerVolume = useCallback((vol: number) => {
    mixerRef.current?.setSpeakerVolume(vol);
  }, []);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    transcripts,
    currentPartial,
    isListening,
    error,
    start,
    stop,
    startMonitor,
    stopMonitor,
    setSpeakersMuted,
    setSpeakerVolume,
    enabled,
    debugInfo,
    monitoring,
    analyserInput,
    analyserSpeakers,
    analyserGladia,
  };
}
